import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  HostListener,
  Injector,
  OnInit
} from '@angular/core';
import { FormBuilder, FormGroup, FormArray, Validators, AbstractControl, AsyncValidatorFn, ValidationErrors } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { DevicesClass } from '@app/core/classes';
import { BreadcrumbsDataModel } from '@app/core/models/breadcrumbs';
import { RoutingConfig } from '@app/routing/routing.config';
import { DialogService } from 'primeng/dynamicdialog';
import { DialogIncludesTypes } from '@app/core/models/diallog';
import { NotificationService } from '@app/modules/notification-modules/notification.service';
import { InputValidators } from '@app/core/validators/input.validator';
import { PerformersService } from '@app/modules/account-modules/performers.service';
import { CitiesService } from '@app/modules/cities-module/cities.service';
import { FundamentalsService } from '@app/modules/fundamentals-modules/fundamentals.service';
import { CityModel, CitiesModel, GenreModel } from '@app/modules/fundamentals-modules/fundamentals.models';
import { SearchGenreComponent } from '@app/modules/genres-modules/search-genre/search-genre.component';
import { ReplaySubject, Observable, of, timer, forkJoin } from 'rxjs';
import { map, catchError, switchMap, finalize } from 'rxjs/operators';
import { BookingService } from '@app/modules/booking-modules/booking.service';
import { PerformerRateDto } from '@app/modules/account-modules/performers.model';
import { URLLinkValidate } from '@app/utils/validator';
import { ICanDeactivateWithUnsaved } from '@app/core/services/guard/guard-unsaved-form.service';
import { SocialPlatform, UpdatePerformerProfileDto, PerformerProfileDto, PhotoVm } from '@app/modules/account-modules/performers.model';
import { PerformerSlugEditDialogComponent } from './dialogs/slug-edit-dialog/slug-edit-dialog.component';
import { MainPhotoEditDialogComponent } from './dialogs/main-photo-edit-dialog/main-photo-edit-dialog.component';
import { GalleryEditDialogComponent } from './dialogs/gallery-edit-dialog/gallery-edit-dialog.component';
import { PERFORMER_EDIT_STATE_SLUG, PERFORMER_EDIT_STATE_IS_PUBLISHED } from '@app/core/services/guard/guard-performer-edit.service';
import { AppStaticURL } from '@app/app.settings';
import { PERFORMERS_PREVIEW_STORAGE_KEY } from '@app/modules/performers-modules/client/performers-preview/performers-preview.model';
import { PerformersPreviewService } from '@app/modules/performers-modules/client/performers-preview/performers-preview.service';
import {
  PERFORMER_TYPES,
  SOCIAL_PLATFORMS,
  PLATFORM_ICON_MAP,
  PlatformIconConfig
} from '../performer-form.constants';

@UntilDestroy()
@Component({
  selector: 'app-account-performers-edit',
  templateUrl: './performers-edit.component.html',
  styleUrls: ['./performers-edit.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PerformersEditComponent extends DevicesClass implements OnInit, ICanDeactivateWithUnsaved {
  readonly Routing = RoutingConfig;
  readonly PerformerTypes = PERFORMER_TYPES;
  readonly SocialPlatforms = SOCIAL_PLATFORMS;
  readonly platformIconMap = PLATFORM_ICON_MAP;

  getPlatformIcon(platformValue: number): PlatformIconConfig {
    return this.platformIconMap[platformValue] ?? { type: 'svg', src: 'assets/images/icons/website.svg' };
  }

  BreadcrumbsData: BreadcrumbsDataModel = [
    { title: 'Личный кабинет', url: '/' + RoutingConfig.Account },
    { title: 'Мои исполнители', url: '/' + RoutingConfig.Account + '/' + RoutingConfig.AccountPerformers },
    { title: 'Редактирование', url: '', active: true }
  ];

  form: FormGroup;
  globalError: string = null;
  submitLoading = false;
  formSubmitted = false;
  showHashtagLimitMessage = false;
  slugPreviewUrl = '';
  slugLinkCopied = false;
  private copyMsgTimer: ReturnType<typeof setTimeout> = null;
  editPerformerId: string | null = null;
  editIsPublished = false;
  /** True when isPublished was set from navigation state (card); then we don't overwrite it from API. */
  private hasIsPublishedFromState = false;
  publishToggleLoading = false;
  editProfileLoading = false;

  Cities: CitiesModel = [];
  CitySelected: CityModel = null;

  GenresSelected: GenreModel[] = [];
  GenresSubject = new ReplaySubject<GenreModel[]>(1);

  MainPhotoPreview: string = null;
  MainPhotoKey: string | null = null;
  GalleryPreviews: string[] = [];
  GalleryKeys: string[] = [];

  biographyLimit = 5000;
  bookingForm: FormGroup | null = null;
  bookingDescriptionLimit = 300;
  bookingSettingsLoading = false;
  bookingSubmitLoading = false;
  bookingGlobalError: string | null = null;
  bookingFormSubmitted = false;

  private readonly formFieldOrder: (string | { array: string })[] = [
    'cityId',
    'name',
    'slug',
    'biography',
    'genreIds',
    'performerTypes',
    'hashtagsRaw',
    { array: 'socialLinks' },
    { array: 'videoLinks' },
    'playlistLink'
  ];

  constructor(
    injector: Injector,
    public cdr: ChangeDetectorRef,
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private dialogService: DialogService,
    private inputValidators: InputValidators,
    private performersService: PerformersService,
    private citiesService: CitiesService,
    private fundamentalsService: FundamentalsService,
    private notificationService: NotificationService,
    private performersPreviewService: PerformersPreviewService,
    private bookingService: BookingService
  ) {
    super(injector);
  }

  ngOnInit(): void {
    this.editPerformerId = this.route.snapshot.paramMap.get('id') || null;
    this.buildForm();
    this.buildBookingForm();
    this.applySlugFromNavigationState();
    this.loadCities();
    this.subscribeGenres();
    this.subscribeSlugPreview();
    this.subscribeBiographyLimit();
    if (this.editPerformerId) {
      this.loadEditProfile();
    }
  }

  hasUnsavedChanges(): boolean {
    return (this.form && this.form.dirty) || (this.bookingForm && this.bookingForm.dirty);
  }

  get showPerformerTypesError(): boolean {
    const ctrl = this.form?.get('performerTypes');
    return !!ctrl && ctrl.invalid && (ctrl.touched || this.formSubmitted);
  }

  @HostListener('window:beforeunload', ['$event'])
  onBeforeUnload($event: BeforeUnloadEvent): void {
    if (this.hasUnsavedChanges()) {
      $event.preventDefault();
    }
  }

  private buildForm(): void {
    this.form = this.fb.group({
      cityId: [0, [Validators.required, this.inputValidators.checkIdValue]],
      name: ['', [Validators.required, Validators.minLength(1), Validators.maxLength(200)]],
      biography: ['', [Validators.maxLength(5000)]],
      socialLinks: this.fb.array([]),
      slug: ['', [Validators.required, Validators.maxLength(100), this.inputValidators.checkSlugValue.bind(this.inputValidators)], [this.slugUniquenessValidator()]],
      genreIds: [[], [Validators.required, (c) => (c.value && c.value.length >= 1 && c.value.length <= 5 ? null : { genreCount: true })]],
      performerTypes: [[], [Validators.required, (c) => (c.value && c.value.length >= 1 && c.value.length <= 3 ? null : { performerTypeCount: true })]],
      videoLinks: this.fb.array([
        this.fb.control('', this.inputValidators.checkVKVideoValue.bind(this.inputValidators)),
        this.fb.control('', this.inputValidators.checkVKVideoValue.bind(this.inputValidators))
      ]),
      playlistLink: ['', [Validators.required, this.inputValidators.checkVKPlaylistValue.bind(this.inputValidators)]],
      hashtags: [[], this.inputValidators.checkHashtagsValue.bind(this.inputValidators)],
      hashtagsRaw: ['']
    });
  }

  private applySlugFromNavigationState(): void {
    if (!this.editPerformerId) return;
    const state = typeof history !== 'undefined' ? history.state : {};
    const slug = state?.[PERFORMER_EDIT_STATE_SLUG];
    if (slug != null && typeof slug === 'string') {
      const normalized = slug.toLowerCase().trim().replace(/[^a-z0-9-]/g, '');
      this.form.get('slug').setValue(normalized, { emitEvent: true });
    }
    const isPublished = state?.[PERFORMER_EDIT_STATE_IS_PUBLISHED];
    if (typeof isPublished === 'boolean') {
      this.editIsPublished = isPublished;
      this.hasIsPublishedFromState = true;
    }
    this.cdr.markForCheck();
  }

  private loadEditProfile(): void {
    if (!this.editPerformerId) return;
    this.editProfileLoading = true;
    this.cdr.markForCheck();
    this.performersService.getPerformerProfileById(this.editPerformerId).pipe(untilDestroyed(this)).subscribe(
      profile => {
        if (!profile) {
          this.editProfileLoading = false;
          this.globalError = 'Профиль не найден.';
          this.cdr.markForCheck();
          return;
        }
        this.applyProfileToForm(profile);
        this.loadBookingSettings();
        this.editProfileLoading = false;
        this.cdr.markForCheck();
      },
      err => {
        this.editProfileLoading = false;
        this.globalError = (err && (err.message || err.error)) || 'Не удалось загрузить профиль.';
        this.cdr.markForCheck();
      }
    );
  }

  private applyProfileToForm(profile: PerformerProfileDto): void {
    const cityId = Number(profile.city) || 0;
    const genreIds = (profile.genres ?? []).map(g => Number(g)).filter(g => !Number.isNaN(g));
    const performerTypes = (profile.performerTypes ?? []).map(t => Number(t)).filter(t => !Number.isNaN(t));
    const slug = (profile.slug ?? '').toString().toLowerCase().trim();

    this.loadCities();
    this.CitySelected = this.citiesService.onGetCityByDictionary(cityId) || this.CitySelected;
    this.GenresSelected = genreIds.map(
      gid => this.fundamentalsService.genresDependencies?.find((g: { id: number }) => g.id === gid)
    ).filter(Boolean) as GenreModel[];

    this.form.patchValue({
      name: (profile.name ?? '').toString(),
      cityId,
      biography: (profile.bio ?? '').toString(),
      slug: slug || (this.form.get('slug').value ?? ''),
      genreIds,
      performerTypes,
      playlistLink: profile.musicPlaylistUrl ?? ''
    });

    const socialLinks = profile.socialLinks ?? [];
    const arr = this.socialLinksArray;
    while (arr.length) arr.removeAt(0);
    for (const link of socialLinks) {
      const platform = link.platform != null ? Number(link.platform) : SocialPlatform.Instagram;
      const url = (link.url ?? '').toString();
      arr.push(this.fb.group({
        platform: [platform],
        url: [url, (c: AbstractControl) => (c.value && !URLLinkValidate(c.value) ? { urlInvalid: true } : null)]
      }));
    }

    const videos = (profile.videos ?? []).slice().sort((a, b) => (Number(a.order) ?? 0) - (Number(b.order) ?? 0));
    const videoCtrl = this.form.get('videoLinks') as FormArray;
    if (videoCtrl && videoCtrl.length >= 2) {
      videoCtrl.get([0]).setValue((videos[0]?.url ?? '').toString());
      videoCtrl.get([1]).setValue((videos[1]?.url ?? '').toString());
    }

    const hashTags = (profile.hashTags ?? []).map(t => (t.startsWith('#') ? t.slice(1) : t).toString());
    this.form.get('hashtags').setValue(hashTags);
    this.form.get('hashtagsRaw').setValue(this.formatHashtagsForInput(profile.hashTags ?? []), { emitEvent: false });

    if (!this.hasIsPublishedFromState) {
      this.editIsPublished = profile.isPublished ?? false;
    }

    const toUrl = (p: string | undefined) => (p && !p.startsWith('http') ? AppStaticURL + p : p) || '';
    const img = profile.image;
    this.MainPhotoPreview = img ? (toUrl(img.path) || toUrl(img.thumbnailPath)) : null;
    this.GalleryPreviews = (profile.photos ?? []).map(p => toUrl(p.thumbnailPath) || toUrl(p.path)).filter(Boolean);
    this.GalleryKeys = profile.photoKeys ?? (profile.photos ?? []).map((p: PhotoVm) => p.key).filter(Boolean) as string[];
  }

  onToggleEditPublish(): void {
    if (!this.editPerformerId || this.publishToggleLoading) return;
    const next = !this.editIsPublished;
    this.publishToggleLoading = true;
    this.cdr.markForCheck();
    this.performersService.setPerformerPublished(this.editPerformerId, next).pipe(untilDestroyed(this)).subscribe(
      () => {
        this.editIsPublished = next;
        this.publishToggleLoading = false;
        this.cdr.markForCheck();
      },
      () => {
        this.publishToggleLoading = false;
        this.cdr.markForCheck();
      }
    );
  }

  private loadCities(): void {
    const deps = this.fundamentalsService.citiesDependencies;
    if (deps && deps.length) {
      this.Cities = [{ name: 'Выбрать город', id: 0 }, ...deps];
      if (!this.CitySelected && this.form.get('cityId').value) {
        this.CitySelected = this.citiesService.onGetCityByDictionary(this.form.get('cityId').value);
      }
      this.cdr.markForCheck();
    }
  }

  private subscribeGenres(): void {
    this.GenresSubject.pipe(untilDestroyed(this)).subscribe(emit => {
      const genres = (emit && (emit as any).genres) ? (emit as any).genres : [];
      this.GenresSelected = Array.isArray(genres) ? genres : [];
      this.form.get('genreIds').setValue(this.GenresSelected.map((g: GenreModel) => g.id));
      this.cdr.markForCheck();
    });
  }

  private subscribeSlugPreview(): void {
    this.form.get('slug').valueChanges.pipe(untilDestroyed(this)).subscribe(val => {
      const s = (val || '').toString().toLowerCase().replace(/[^a-z0-9-]/g, '');
      this.slugPreviewUrl = s ? `muzrus.com/${s}` : '';
      this.cdr.markForCheck();
    });
  }

  private subscribeBiographyLimit(): void {
    this.form.get('biography').valueChanges.pipe(untilDestroyed(this)).subscribe(value => {
      this.biographyLimit = value ? 5000 - value.length : 5000;
      this.cdr.markForCheck();
    });
  }

  private slugUniquenessValidator(): AsyncValidatorFn {
    return (control: AbstractControl): Observable<ValidationErrors | null> => {
      const slug = (control.value || '').toString().toLowerCase().trim();
      if (!slug || slug.length < 3) return of(null);
      return timer(200).pipe(
        switchMap(() => this.performersService.checkSlug(slug)),
        map(res => (res?.isTaken ? { slugTaken: true } : null)),
        catchError(() => of(null))
      );
    };
  }

  get socialLinksArray(): FormArray {
    return this.form.get('socialLinks') as FormArray;
  }

  get videoLinksArray(): FormArray {
    return this.form.get('videoLinks') as FormArray;
  }

  addSocialLink(): void {
    this.socialLinksArray.push(this.fb.group({
      platform: [SocialPlatform.Instagram],
      url: ['', (c) => (c.value && !URLLinkValidate(c.value) ? { urlInvalid: true } : null)]
    }));
  }

  removeSocialLink(i: number): void {
    this.socialLinksArray.removeAt(i);
  }

  trackByIndex(index: number): number {
    return index;
  }

  onCityDropdownChange(): void {
    this.form.get('cityId').setValue(this.CitySelected ? this.CitySelected.id : 0);
    this.cdr.markForCheck();
  }

  openGenreSearch(): void {
    this.dialogService.open(SearchGenreComponent, {
      header: 'Выберите до 5 жанров',
      width: '90%',
      styleClass: 'p-dialog__genre-search',
      data: {
        includeType: DialogIncludesTypes.ComponentTemplate,
        includeData: {
          dataInitial: { genres: this.GenresSelected },
          dataSubject: this.GenresSubject,
          maxSelection: 5
        }
      }
    });
  }

  togglePerformerType(value: number): void {
    const ctrl = this.form.get('performerTypes');
    const arr: number[] = ctrl.value || [];
    const idx = arr.indexOf(value);
    if (idx === -1) {
      if (arr.length >= 3) return;
      ctrl.setValue([...arr, value]);
    } else {
      ctrl.setValue(arr.filter(x => x !== value));
    }
    ctrl.markAsDirty();
    ctrl.markAsTouched();
    this.cdr.markForCheck();
  }

  isPerformerTypeSelected(value: number): boolean {
    return (this.form.get('performerTypes').value || []).indexOf(value) !== -1;
  }

  isPerformerTypeDisabled(value: number): boolean {
    const arr: number[] = this.form.get('performerTypes').value || [];
    return arr.length >= 3 && arr.indexOf(value) === -1;
  }

  openSlugEditDialog(): void {
    if (!this.editPerformerId) return;
    const currentSlug = (this.form.get('slug').value || '').toString().toLowerCase().trim();
    const ref = this.dialogService.open(PerformerSlugEditDialogComponent, {
      header: 'Изменить slug',
      width: '420px',
      data: { performerId: this.editPerformerId, currentSlug }
    });
    ref.onClose.pipe(untilDestroyed(this)).subscribe((newSlug: string | undefined) => {
      if (newSlug != null) {
        this.form.patchValue({ slug: newSlug });
        this.cdr.markForCheck();
      }
    });
  }

  onCopySlugLink(): void {
    const slug = (this.form.get('slug').value || '').toString().toLowerCase().trim();
    if (!slug) return;
    const url = this.performersService.getPublicProfileUrl(slug);
    const done = () => {
      this.slugLinkCopied = true;
      this.cdr.markForCheck();
      if (this.copyMsgTimer) clearTimeout(this.copyMsgTimer);
      this.copyMsgTimer = setTimeout(() => {
        this.slugLinkCopied = false;
        this.cdr.markForCheck();
      }, 2000);
    };
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(url).then(done).catch(() => {});
    }
  }

  openMainPhotoEditDialog(): void {
    if (!this.editPerformerId) return;
    const ref = this.dialogService.open(MainPhotoEditDialogComponent, {
      header: 'Основное фото',
      width: '50vw',
      data: {
        preview: this.MainPhotoPreview,
        key: this.MainPhotoKey,
        performerId: this.editPerformerId
      }
    });
    ref.onClose.pipe(untilDestroyed(this)).subscribe((result: { applied?: boolean } | undefined) => {
      if (result?.applied) {
        this.loadEditProfile();
      }
      this.cdr.markForCheck();
    });
  }

  openGalleryEditDialog(): void {
    if (!this.editPerformerId) return;
    const ref = this.dialogService.open(GalleryEditDialogComponent, {
      header: 'Фотоальбом',
      width: '50vw',
      data: {
        performerId: this.editPerformerId,
        currentPhotoUrls: this.GalleryPreviews || [],
        currentKeys: this.GalleryKeys || []
      }
    });
    ref.onClose.pipe(untilDestroyed(this)).subscribe((result: { applied?: boolean } | undefined) => {
      if (result?.applied) {
        this.loadEditProfile();
      }
      this.cdr.markForCheck();
    });
  }

  private readonly maxHashtags = 10;

  parseHashtagsInput(val: string): { withHash: string[]; withoutHash: string[]; excess: boolean } {
    const tokens = (val || '')
      .split(/\s+/)
      .map(s => s.trim().replace(/^#+/, ''))
      .filter(s => s.length > 0);
    const limited = tokens.slice(0, this.maxHashtags);
    const withHash = limited.map(t => '#' + t);
    const withoutHash = limited;
    return { withHash, withoutHash, excess: tokens.length > this.maxHashtags };
  }

  formatHashtagsForInput(tagsWithHash: string[]): string {
    return tagsWithHash.map(t => (t.startsWith('#') ? t : '#' + t)).join(' ');
  }

  onHashtagsInput(): void {
    const rawControl = this.form.get('hashtagsRaw');
    if (!rawControl) return;
    const parsed = this.parseHashtagsInput(rawControl.value);
    if (parsed.excess) {
      rawControl.setValue(this.formatHashtagsForInput(parsed.withHash), { emitEvent: false });
      this.form.get('hashtags').setValue(parsed.withoutHash);
      this.showHashtagLimitMessage = true;
      this.cdr.markForCheck();
    } else {
      this.showHashtagLimitMessage = false;
      this.cdr.markForCheck();
    }
  }

  onHashtagsBlur(): void {
    const rawControl = this.form.get('hashtagsRaw');
    if (!rawControl) return;
    const { withHash, withoutHash } = this.parseHashtagsInput(rawControl.value);
    const hashtagsControl = this.form.get('hashtags');
    hashtagsControl.setValue(withoutHash);
    hashtagsControl.markAsTouched();
    rawControl.setValue(this.formatHashtagsForInput(withHash), { emitEvent: false });
    this.showHashtagLimitMessage = false;
    this.cdr.markForCheck();
  }

  private scrollToFirstInvalidField(): void {
    const scroll = () => {
      for (const key of this.formFieldOrder) {
        const controlName = typeof key === 'string' ? key : key.array;
        const control = this.form.get(controlName);
        if (control && control.invalid) {
          const el = document.getElementById('performers-edit-field-' + controlName);
          if (el) {
            el.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
          return;
        }
      }
      const globalEl = document.querySelector('.performers-edit__global-error');
      if (globalEl) {
        (globalEl as HTMLElement).scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    };
    setTimeout(scroll, 0);
  }

  onPreview(): void {
    this.formSubmitted = true;
    this.form.markAllAsTouched();
    this.form.updateValueAndValidity();
    this.form.get('performerTypes').updateValueAndValidity();
    if (this.form.invalid) {
      this.scrollToFirstInvalidField();
      this.cdr.markForCheck();
      return;
    }
    const v = this.form.getRawValue();
    const genreIds = (v.genreIds || []) as number[];
    const performerTypes = (v.performerTypes || []) as number[];
    if (!genreIds.length || genreIds.length > 5) {
      this.cdr.markForCheck();
      return;
    }
    if (!performerTypes.length || performerTypes.length > 3) {
      this.cdr.markForCheck();
      return;
    }
    const socialLinks = (v.socialLinks || []).map((item: { platform: number; url: string }) =>
      ({ platform: typeof item.platform === 'number' ? item.platform : SocialPlatform.Instagram, url: item.url })).filter((item: { url: string }) => !!item.url);
    const videoLinks = (v.videoLinks || []).filter(Boolean).map((url: string, i: number) => ({ url: (url && url.trim()) || '', order: i })).filter(link => !!link.url);
    const hashtagsRaw = (v.hashtags || []) as string[];
    const hashTagsWithHash = hashtagsRaw.map(t => (t.startsWith('#') ? t : '#' + t));

    const profileDto: PerformerProfileDto = {
      name: v.name || '',
      city: v.cityId,
      bio: v.biography || undefined,
      image: this.MainPhotoPreview ? { path: this.MainPhotoPreview, thumbnailPath: this.MainPhotoPreview } : undefined,
      photos: (this.GalleryPreviews || []).map(url => ({ path: url, thumbnailPath: url })),
      genres: genreIds,
      performerTypes,
      videos: videoLinks.map((link, i) => ({ url: link.url, order: link.order })),
      hashTags: hashTagsWithHash,
      musicPlaylistUrl: v.playlistLink?.trim() ?? '',
      socialLinks
    };
    try {
      this.performersPreviewService.setPreviewData(profileDto);
      localStorage.setItem(PERFORMERS_PREVIEW_STORAGE_KEY, JSON.stringify(profileDto));
      const url = `${window.location.origin}/performers-preview`;
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (e) {
      this.notificationService.onDialog({
        type: DialogIncludesTypes.PopupErrorTemplate,
        header: 'Ошибка',
        content: 'Не удалось открыть предпросмотр.'
      });
    }
    this.cdr.markForCheck();
  }

  onSubmit(): void {
    this.globalError = null;
    this.formSubmitted = true;
    this.form.markAllAsTouched();
    this.form.updateValueAndValidity();
    this.form.get('performerTypes').updateValueAndValidity();
    if (this.form.invalid) {
      this.scrollToFirstInvalidField();
      this.cdr.markForCheck();
      return;
    }
    const v = this.form.getRawValue();
    const genreIds = (v.genreIds || []) as number[];
    const performerTypes = (v.performerTypes || []) as number[];
    if (!genreIds.length || genreIds.length > 5) {
      this.globalError = 'Выберите от 1 до 5 жанров.';
      this.scrollToFirstInvalidField();
      this.cdr.markForCheck();
      return;
    }
    if (!performerTypes.length || performerTypes.length > 3) {
      this.globalError = 'Выберите от 1 до 3 типов исполнителя.';
      this.scrollToFirstInvalidField();
      this.cdr.markForCheck();
      return;
    }
    this.submitLoading = true;
    this.cdr.markForCheck();

    const socialLinks = (v.socialLinks || []).map((item: { platform: number; url: string }) =>
      ({ platform: typeof item.platform === 'number' ? item.platform : SocialPlatform.Instagram, url: item.url })).filter((item: { url: string }) => !!item.url);
    const videoLinks = (v.videoLinks || []).filter(Boolean).map((url: string, i: number) => ({ url: (url && url.trim()) || '', order: i })).filter(link => !!link.url);

    const updatePayload: UpdatePerformerProfileDto = {
      name: (v.name && v.name.trim()) || '',
      city: v.cityId,
      bio: v.biography?.trim() || undefined,
      socialLinks: socialLinks.length ? socialLinks : undefined,
      genres: genreIds,
      performerTypes,
      videos: videoLinks.length ? videoLinks : undefined,
      musicPlaylistUrl: v.playlistLink?.trim() || undefined,
      hashTags: (v.hashtags || []).length ? v.hashtags : undefined
    };
    this.performersService.updatePerformer(this.editPerformerId, updatePayload).pipe(untilDestroyed(this)).subscribe(
      () => {
        this.submitLoading = false;
        this.form.markAsPristine();
        this.notificationService.onDialog({
          type: DialogIncludesTypes.PopupSuccessTemplate,
          header: 'Изменения сохранены',
          content: 'Профиль исполнителя успешно обновлён.',
          icons: 'pi pi-check'
        });
        this.cdr.markForCheck();
      },
      err => {
        this.submitLoading = false;
        this.globalError = (err && (err.message || err.error)) || 'Не удалось сохранить изменения. Попробуйте позже.';
        this.cdr.markForCheck();
      }
    );
  }

  private buildBookingForm(): void {
    const optionalPositiveAmount = (c: AbstractControl) => {
      const v = c.value;
      if (v == null || v === '') {
        return null;
      }
      const n = Number(v);
      return !Number.isNaN(n) && n > 0 ? null : { positiveAmount: true };
    };
    this.bookingForm = this.fb.group({
      pricePerHour: [null as number | null, optionalPositiveAmount],
      pricePerSet: [null as number | null, optionalPositiveAmount],
      bookingDescription: ['', Validators.maxLength(300)],
      bookingAvailabilityText: ['', Validators.maxLength(100)]
    });
    this.bookingForm.setValidators(() => this.bookingRateValidator());
    this.bookingForm
      .get('bookingDescription')
      ?.valueChanges.pipe(untilDestroyed(this))
      .subscribe(value => {
        this.bookingDescriptionLimit = value ? 300 - String(value).length : 300;
        this.cdr.markForCheck();
      });
  }

  private bookingRateValidator(): ValidationErrors | null {
    if (!this.bookingForm) {
      return null;
    }
    const ph = this.bookingForm.get('pricePerHour')?.value;
    const ps = this.bookingForm.get('pricePerSet')?.value;
    const desc = (this.bookingForm.get('bookingDescription')?.value ?? '').toString().trim();
    const hasPh = ph != null && ph !== '' && !Number.isNaN(Number(ph)) && Number(ph) > 0;
    const hasPs = ps != null && ps !== '' && !Number.isNaN(Number(ps)) && Number(ps) > 0;
    if (hasPh || hasPs || desc.length > 0) {
      return null;
    }
    return { bookingRateRequired: true };
  }

  private loadBookingSettings(): void {
    const id = this.editPerformerId;
    if (!id || !this.bookingForm) {
      return;
    }
    this.bookingSettingsLoading = true;
    this.bookingGlobalError = null;
    this.cdr.markForCheck();
    this.bookingService
      .getPerformerBookingSettings(id)
      .pipe(
        finalize(() => {
          this.bookingSettingsLoading = false;
          this.cdr.markForCheck();
        }),
        untilDestroyed(this)
      )
      .subscribe(
        data => {
          const rates = data.rates;
          this.bookingForm!.patchValue(
            {
              pricePerHour: rates?.pricePerHour ?? null,
              pricePerSet: rates?.pricePerSet ?? null,
              bookingDescription: rates?.description ?? '',
              bookingAvailabilityText: data.availability ?? ''
            },
            { emitEvent: false }
          );
          const desc = (rates?.description ?? '').toString();
          this.bookingDescriptionLimit = desc ? 300 - desc.length : 300;
          this.bookingForm!.markAsPristine();
          this.cdr.markForCheck();
        },
        () => {
          this.bookingGlobalError = 'Не удалось загрузить настройки бронирования.';
        }
      );
  }

  onSaveBookingSettings(): void {
    const id = this.editPerformerId;
    if (!id || !this.bookingForm || this.bookingSubmitLoading) {
      return;
    }
    this.bookingFormSubmitted = true;
    this.bookingGlobalError = null;
    if (this.bookingForm.invalid) {
      this.bookingForm.markAllAsTouched();
      this.cdr.markForCheck();
      return;
    }
    const v = this.bookingForm.getRawValue();
    const ratePayload = this.buildBookingRatePayload(v);
    const availabilityText = String(v.bookingAvailabilityText || '').trim();

    this.bookingSubmitLoading = true;
    this.cdr.markForCheck();

    forkJoin({
      rate: this.bookingService.updatePerformerBookingRate(id, ratePayload),
      availability: this.bookingService.updatePerformerBookingAvailability(id, {
        availability: availabilityText || null
      })
    })
      .pipe(
        finalize(() => {
          this.bookingSubmitLoading = false;
          this.cdr.markForCheck();
        }),
        untilDestroyed(this)
      )
      .subscribe(
        () => {
          this.bookingFormSubmitted = false;
          this.bookingForm!.markAsPristine();
          this.notificationService.onDialog({
            type: DialogIncludesTypes.PopupSuccessTemplate,
            header: 'Сохранено',
            content: 'Настройки бронирования обновлены.'
          });
        },
        err => {
          this.bookingGlobalError =
            (err && (err.message || err.error?.message)) || 'Не удалось сохранить настройки бронирования.';
        }
      );
  }

  private buildBookingRatePayload(v: Record<string, unknown>): PerformerRateDto {
    const ph = v.pricePerHour;
    const ps = v.pricePerSet;
    const desc = (v.bookingDescription ?? '').toString().trim();
    const pricePerHour =
      ph != null && ph !== '' && !Number.isNaN(Number(ph)) ? Number(ph) : undefined;
    const pricePerSet =
      ps != null && ps !== '' && !Number.isNaN(Number(ps)) ? Number(ps) : undefined;
    return { pricePerHour, pricePerSet, description: desc };
  }
}
